import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Tab names
const tabs = ["Hotel", "Restaurant", "Cafeteria"];

// Menu buttons (copy your full menuButtons1 here)
const menuButtons1 = [
  // Your menuButtons1 array goes here...
];

// Knowledge content (common + type-specific)
const knowledgeBase = {
  "Startup Essentials": {
    common: [
      "Legal & Licensing: FSSAI, GST, local permits",
      "Funding & Investment: MSME loans, private investment",
      "Location Selection & Market Study",
      "Basic Infrastructure Planning",
    ],
    Hotel: [
      "Room planning & front desk setup",
      "Housekeeping standards & workflow",
      "Guest experience & concierge planning",
    ],
    Restaurant: [
      "Kitchen layout & workflow",
      "Menu planning & pricing",
      "Table management & reservation systems",
    ],
    Cafeteria: [
      "Quick service design",
      "Grab-and-go layout",
      "Beverage station planning",
    ],
  },
  "Departments / Teams": {
    common: [
      "Frontline staff: customer interaction",
      "Kitchen & Culinary team",
      "Procurement & Inventory management",
      "Finance & Admin team",
    ],
    Hotel: ["Concierge", "Housekeeping", "Maintenance staff"],
    Restaurant: ["Chef team", "Servers", "Bar/counter staff"],
    Cafeteria: ["Minimal staff, multitasking roles", "Barista/coffee station"],
  },
  "Operations & Workflow": {
    common: [
      "Standard Operating Procedures (SOPs)",
      "Inventory & supply chain management",
      "Quality control & safety checks",
    ],
    Hotel: ["Room service workflow", "Guest booking management", "Guest experience tracking"],
    Restaurant: ["Table orders & reservation workflow", "Kitchen prep schedule", "Order accuracy control"],
    Cafeteria: ["Fast service flow", "Peak-hour management", "Quick inventory replenishment"],
  },
  "Marketing & Customer Psychology": {
    common: [
      "Branding & identity creation",
      "Customer feedback & loyalty programs",
      "Social media & digital marketing",
    ],
    Hotel: ["Package deals", "Event promotions", "Online guest reviews"],
    Restaurant: ["Menu marketing", "Dining experience promotions", "Food reviews & ratings"],
    Cafeteria: ["Daily specials", "Upselling snacks/beverages", "Quick promotions"],
  },
  "Compliance & Challenges": {
    common: [
      "Food safety & hygiene compliance",
      "Staff training & retention",
      "Accounting & taxation compliance",
    ],
    Hotel: ["Fire safety & occupancy regulations", "Guest security"],
    Restaurant: ["Alcohol license management", "Hygiene audits"],
    Cafeteria: ["Quick labeling compliance", "Minimal local permits"],
  },
  "Advanced Insights / Digital Tools": {
    common: [
      "Analytics & KPI tracking",
      "AI dashboards for sales and customer behavior",
      "Gamification & engagement strategies",
    ],
    Hotel: [],
    Restaurant: [],
    Cafeteria: [],
  },
};

// Mapping each knowledge section to platform buttons (menuButtons1)
const platformMapping = {
  "Startup Essentials": ["HORECA Success Kit", "HORECA Hub FAQ", "Starting a HORECA"],
  "Departments / Teams": ["HORECA Workforce", "Consultants", "Payment Dodger DB"],
  "Operations & Workflow": [
    "Table Orders Dashboard",
    "Take Away Orders Dashboard",
    "Home Orders Dashboard",
    "HORECA ERP",
  ],
  "Marketing & Customer Psychology": ["Rewarding Ads", "CSAT", "BoloGrahak", "Forum"],
  "Compliance & Challenges": ["NPA Solutions", "Funding & Loans", "Digital Card"],
  "Advanced Insights / Digital Tools": [
    "Process Scorecard",
    "HORECA Ranking",
    "CommuniCapture",
    "LLM Capture",
  ],
};

const chartData = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May"],
  datasets: [
    {
      label: "HORECA Insights",
      data: [12, 19, 10, 5, 20],
      backgroundColor: "rgba(59, 130, 246, 0.7)",
    },
  ],
};

const HorecaAbout = () => {
  const [activeTab, setActiveTab] = useState("Hotel");
  const [openSections, setOpenSections] = useState({}); // accordion state

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="min-h-screen bg-black text-white p-4">
      {/* Header */}
      <div className="text-center mb-4">
        {/* <div className="text-xl font-bold text-teal-300">HORECA</div> */}
        <div className="text-lg lg:text-xl text-yellow-200">All About Hotel. Restaurant. Cafeteria</div>
        {/* <div className="text-[11px] text-yellow-200">
          Owner Centric. Transparency. Connecting Skill with Productivity
        </div> */}
      </div>

      {/* Tab Buttons */}
      <div className="flex flex-row justify-center gap-12 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
              activeTab === tab
                ? "bg-yellow-400 text-black"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Knowledge Base Accordions */}
      {Object.keys(knowledgeBase).map((section) => (
        <div key={section} className="mb-3">
          <button
            onClick={() => toggleSection(section)}
            className="w-full flex justify-between items-center bg-gray-900 p-3 rounded-lg font-semibold hover:bg-yellow-600 transition-colors"
          >
            {section}
            <span>{openSections[section] ? "▲" : "▼"}</span>
          </button>

          {openSections[section] && (
            <div className="bg-gray-800 p-3 rounded-b-lg space-y-2 text-sm text-gray-300">
              {/* Knowledge Content */}
              <ul className="list-disc list-inside">
                {knowledgeBase[section].common.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
                {knowledgeBase[section][activeTab]?.map((item, idx) => (
                  <li key={idx} className="text-yellow-300">
                    {item} (Specific to {activeTab})
                  </li>
                ))}
              </ul>

              {/* Platform Buttons */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                {platformMapping[section].map((btnLabel) => {
                  const btn = menuButtons1.find((b) => b.label === btnLabel);
                  if (!btn) return null;
                  return (
                    <Link
                      key={btn.label}
                      to={btn.link}
                      className="bg-gray-700 hover:bg-yellow-500 text-white p-2 rounded-lg text-center flex flex-col items-center justify-center text-xs transition-all"
                    >
                      <span className="text-lg">{btn.emoji}</span>
                      <span>{btn.label}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Operational Insights Chart */}
      <div className="bg-gray-900 p-4 rounded-lg my-6">
        <div className="text-teal-300 font-semibold mb-2">HORECA Insights Dashboard</div>
        <Bar data={chartData} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
      </div>
    </div>
  );
};

export default HorecaAbout;
